import React, { useEffect, useRef } from "react";
import { Text, Flex, type Icon } from "@chakra-ui/react";
import { LeversIcon, PersonWithCheckIcon, RefreshIcon } from "ui/icons";
import { type BrandColorToken } from "theme/colors";
import { animate, useInView, useMotionValue, useTransform } from "framer-motion";
import { ChakraSpan } from "ui/chakra-framer";
import { customersCount } from "../../constants/customers";

export function HightouchStats() {
  const scope = useRef<HTMLDivElement>(null);
  const isInView = useInView(scope, { amount: 0.3, once: true });

  return (
    <Flex
      ref={scope}
      flexDir="column"
      boxSize="100%"
      bg="new.green.950"
      gap={{ base: "smaller", lg: "small" }}
      p={{ base: "smaller", sm: "small", md: "smaller", lg: "small" }}
    >
      <AnimatedStatRow
        isInView={isInView}
        bg="new.yellow.150"
        icon={PersonWithCheckIcon}
        startNumber={803}
        endNumber={customersCount}
        description="Customers sync data with Hightouch"
        animationDuration={1.25}
      />
      <AnimatedStatRow
        isInView={isInView}
        bg="new.violet.300"
        icon={LeversIcon}
        startNumber={51_041_921}
        endNumber={51_042_863}
        description="Experiences personalized by AI"
        animationDuration={1.55}
      />
      <AnimatedStatRow
        isInView={isInView}
        bg="new.green.300"
        icon={RefreshIcon}
        startNumber={2_168_675_282_052}
        endNumber={2_168_675_287_222}
        description="Records synced in 2024"
        animationDuration={2}
      />
    </Flex>
  );
}

type AnimatedStatRowProps = {
  isInView: boolean;
  animationDuration: number;
  startNumber: number;
  endNumber: number;
  description: string;
  bg: BrandColorToken;
  icon: typeof Icon;
};

function AnimatedStatRow({
  isInView,
  animationDuration,
  startNumber,
  endNumber,
  description,
  bg,
  icon: StatIcon,
}: AnimatedStatRowProps) {
  const motionValue = useMotionValue(startNumber);
  const formattedValue = useTransform(motionValue, (value) => Math.round(value).toLocaleString());

  useEffect(() => {
    const animation = animate(motionValue, isInView ? endNumber : startNumber, {
      duration: isInView ? animationDuration : 0.05,
    });

    return animation.stop;
  }, [motionValue, isInView, animationDuration, endNumber, startNumber]);
  return (
    <Flex
      bg={bg}
      w="100%"
      flex="1"
      gap={{ base: "smallest", sm: "smaller", xl: "big" }}
      pl={{ base: "smaller", lg: "small" }}
      py="tinier"
      align="center"
      borderRadius={{ base: "smallest", md: "smaller" }}
    >
      <StatIcon boxSize={{ base: "22px", sm: "28px", lg: "42px", xl: "50px" }} />

      <Text>
        <ChakraSpan
          display="block"
          lineHeight="100%"
          fontSize={{ base: "brand.md", sm: "brand.2xl", lg: "brand.4xl", xl: "brand.6xl" }}
          fontWeight="semibold"
          mb={{ base: 0, lg: "tiniest", xl: "tiny" }}
        >
          {formattedValue}
        </ChakraSpan>
        <Text
          as="span"
          display="block"
          textStyle={{ base: "caption1", sm: "body3", md: "caption1", lg: "body3", xl: "body1" }}
        >
          {description}
        </Text>
      </Text>
    </Flex>
  );
}
